


import React, { useState } from 'react';
import { X, Save, Globe } from 'lucide-react';
import TagInput from '../atoms/TagInput';

interface GlobalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    name: string;
    tags: string[];
    configuration: any;
  };
  onSave: (data: { name: string; tags: string[]; configuration: any }) => void;
}

const GlobalSettingsModal: React.FC<GlobalSettingsModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <GlobalSettingsModalInner {...props} />;
};

const GlobalSettingsModalInner: React.FC<GlobalSettingsModalProps> = ({ onClose, data, onSave }) => {
  const [formData, setFormData] = useState(() => ({ ...data }));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [configString, setConfigString] = useState(() => JSON.stringify(data.configuration, null, 2));

  const handleConfigChange = (val: string) => {
    setConfigString(val);
    try {
      const parsed = JSON.parse(val);
      setFormData(prev => ({ ...prev, configuration: parsed }));
      setJsonError(null);
    } catch (e) {
      setJsonError("Invalid JSON format");
    }
  };

  const handleSave = () => {
    if (jsonError) return;
    onSave(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
           <div className="flex items-center gap-2">
             <div className="bg-teal-100 p-1.5 rounded-lg text-teal-700">
                <Globe size={18} />
             </div>
             <h2 className="text-lg font-bold text-slate-900">Global Settings</h2>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
             <X size={20} />
           </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
           <div>
             <label htmlFor="gs-name" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Entitlement Name</label>
             <input
               id="gs-name"
               type="text"
               value={formData.name}
               onChange={e => setFormData({ ...formData, name: e.target.value })}
               className="w-full p-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
             />
           </div>

           <div>
             <label htmlFor="gs-tags" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Global Tags</label>
             <TagInput
               id="gs-tags"
               tags={formData.tags}
               onChange={tags => setFormData({ ...formData, tags: tags })}
               colorClass="bg-teal-50 text-teal-700 border-teal-200"
             />
           </div>

           <div>
             <label htmlFor="gs-config" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Configuration (JSON)</label>
             <textarea
               id="gs-config"
               value={configString}
               onChange={e => handleConfigChange(e.target.value)}
               className={`w-full h-40 p-3 font-mono text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 bg-white text-slate-900 ${jsonError ? 'border-red-500 focus:border-red-500' : 'border-slate-300 focus:border-teal-500'}`}
             />
             {jsonError && <p className="text-red-500 text-xs mt-1">{jsonError}</p>}
           </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 font-medium text-sm hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!!jsonError}
            className="px-4 py-2 bg-teal-600 text-white font-medium text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={16} /> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalSettingsModal;
